import clsx from 'clsx';
import React from 'react';

import ResetIcon from '@/assets/icons/reload.svg';

import { Button } from './button';
import { Skeleton } from './skeleton';
import { Tooltip } from './tooltip';
import { Typography } from './typography';

interface InputProps {
  label?: string;
  description?: string;
  placeholderIcon?: React.ReactNode | string;
  placeholderLabel?: string;
  error?: string;
  showError?: boolean;
  isNumber?: boolean;
  onReset?: () => void;
  resetTooltipContent?: string;
  isFetching?: boolean;
  isBorderless?: boolean;
}

export const Input = React.forwardRef<
  HTMLInputElement,
  InputProps & React.ComponentProps<'input'>
>(
  (
    {
      label,
      description,
      placeholderIcon,
      placeholderLabel,
      error,
      showError = true,
      className,
      isNumber,
      onReset,
      resetTooltipContent,
      isFetching,
      isBorderless,
      ...props
    },
    ref,
  ) => {
    // Function to validate number input
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isNumber) {
        const input = e.currentTarget;
        const originalValue = input.value;
        let value = input.value
          .replace(/[^0-9.]/g, '') // allow only numbers and one dot
          .replace(/^([0-9]*\.?[0-9]*).*$/, '$1');

        const num = parseFloat(value);
        if (typeof props.max === 'number' && num > props.max) {
          value = props.max.toString();
        } else if (typeof props.min === 'number' && num < props.min) {
          value = props.min.toString();
        }

        // If the value starts with a dot, prepend a zero
        if (value.startsWith('.')) value = `0${value}`;

        // Only update if value changed
        if (originalValue !== value) {
          input.value = value;
        }
      }
      // Always call the original onChange handler (from register)
      if (props.onChange) {
        props.onChange(e);
      }
    };

    return (
      <div className={clsx('flex w-full flex-col gap-1', className)}>
        {/* Input label */}
        {label && (
          <label htmlFor={props.id}>
            <Typography variant="subtitle2">
              {label}{' '}
              {props.required && <span className="text-fx-rose">*</span>}
            </Typography>
          </label>
        )}

        {/* Small description below the label */}
        {description && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            {description}
          </Typography>
        )}

        <div className="relative">
          {/* Placeholder icon to the left of the input (eg. $ or %) */}
          {(!!placeholderIcon || !!placeholderLabel) && (
            <div className="text-fx-neutral-400 pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
              {!!placeholderIcon &&
                (typeof placeholderIcon === 'string' ? (
                  <img src={placeholderIcon} alt="Icon" />
                ) : (
                  placeholderIcon
                ))}
              {!!placeholderLabel && (
                <Typography variant="inactive1" className="w-4 text-center">
                  {placeholderLabel}
                </Typography>
              )}
              {isFetching && <Skeleton className="h-4 w-12" />}
            </div>
          )}

          {/* Input component */}
          <input
            ref={ref}
            data-slot="input"
            className={clsx(
              'placeholder:text-fx-neutral-600 flex h-9 w-full min-w-0 border text-base outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
              'border-fx-neutral-300  text-fx-neutral-900 rounded-sm bg-white px-2 py-1.5',
              { 'pl-8': !!placeholderIcon || !!placeholderLabel },
              { '!border-fx-rose': !!error },
              {
                'focus:border-fx-brand-600 focus:shadow-selected focus:border focus:outline-none':
                  !props.readOnly && !isBorderless,
              },
              {
                '!bg-fx-neutral-50': props.readOnly,
              },
              { '!border-none': isBorderless },
            )}
            {...props}
            onChange={handleChange}
            placeholder={isFetching ? '' : props.placeholder}
          />

          {/* Reset button to the right of the input */}
          {!!onReset && (
            <Tooltip content={resetTooltipContent}>
              <Button
                type="button"
                variant="icon"
                size="icon"
                onClick={onReset}
                className="absolute right-0 top-0"
              >
                <img src={ResetIcon} alt="Reset" />
              </Button>
            </Tooltip>
          )}
        </div>

        {/* Error message */}
        {error && showError && (
          <Typography variant="small1" className="!text-fx-rose">
            {error}
          </Typography>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
